import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Edit } from '@material-ui/icons';
import { HeaderIcon, CustomTextField, InputError } from './styles';
import { hideModal } from 'actions/modalActions';
import { updateBookingSelection } from 'actions/schedulerActions';

import { Modal, Button, Typography, Layout, IconButton } from '@hub-mono/ui';
import styled from 'styled-components';
import { isModalView } from 'utils/isModalView';
import { useModalOffset } from 'hooks/useModalOffset';
import classnames from 'classnames';

const StyledModal = styled(Modal)`
    .hub-ui__modal-paper {
        min-width: 853px;
    }
`;

const TabsContainer = styled.div`
    width: 100;
`;

const CommonModalTemplate = props => {
    const {
        title,
        description,
        onSave,
        children,
        loading = false,
        disabledSaveButton,
        saveButtonTooltip,
        additionalButton,
        onHideModalCustom,
        saveButtonText = 'Save',
        saveButtonDataCy,
        extendFooter,
        icon: Icon,
        showSaveButton = true,
        titleEditable = false,
        onTitleChange,
        tabLinks,
        underNavigationComponent,
        fullWidth = 'md',
        fullHeight = true,
        submitOnPressEnter,
        headerLeftComponent,
        withFullScreenToggle = false,
        disableBackdropClick = false,
        hideCloseBtn = false,
        hideCancelBtn = false,
    } = props;
    const inputRef = useRef();
    const dispatch = useDispatch();
    const [isEditMode, setEditMode] = useState(false);
    const [fullScreenModal, setFullScreenModal] = useState(false);

    const toggleEditMode = () => {
        titleEditable && setEditMode(prevState => !prevState);
    };

    const onHideModal = () => {
        if (onHideModalCustom) {
            onHideModalCustom();
        } else {
            dispatch(hideModal());
            dispatch(updateBookingSelection.request());
        }
    };

    useEffect(() => {
        if (isEditMode && inputRef.current) {
            inputRef.current.focus();
        }
    }, [isEditMode]);

    const fullScreen = isModalView();

    const offsetClassNames = useModalOffset(`rounded-0 col-sm-9 col-lg-9 col-md-8 col-xl-10 px-0`, fullScreen);

    return (
        <StyledModal
            disableBackdropClick={disableBackdropClick}
            variant="modal"
            hideCloseButton
            onClose={onHideModal}
            bottomDivider
            backdrop
            fullWidth={fullScreenModal ? 'xl' : fullWidth}
            fullHeight={fullScreenModal ? true : fullHeight}
            topDivider={false}
            className={classnames(
                fullScreenModal ? undefined : offsetClassNames,
                'common-modal-template',
                submitOnPressEnter ? 'modal-submit-enter' : ''
            )}
            title={
                <Layout stack gap={24} stretch>
                    <Layout hAlign="space-between">
                        <Layout stack>
                            <Layout>
                                {Icon && (
                                    <HeaderIcon>
                                        <Icon />
                                    </HeaderIcon>
                                )}
                                {isEditMode || (!isEditMode && !title) ? (
                                    <>
                                        <CustomTextField
                                            value={title}
                                            innerRef={inputRef}
                                            onChange={onTitleChange}
                                            invalid={!title}
                                            onFocus={() => setEditMode(true)}
                                            onBlur={toggleEditMode}
                                        />
                                        {!title && (
                                            <InputError className="invalid-feedback d-block">
                                                This field is required
                                            </InputError>
                                        )}
                                    </>
                                ) : (
                                    <div onClick={toggleEditMode}>
                                        <Typography variant="title" size="large" tagName="h2">
                                            {title}
                                            {titleEditable && <Edit />}
                                        </Typography>
                                    </div>
                                )}
                            </Layout>
                            <Typography variant="body" size="medium">
                                {description}
                            </Typography>
                        </Layout>
                        <Layout gap={24} hAlign="flex-end">
                            {headerLeftComponent}
                            <Layout>
                                {withFullScreenToggle ? (
                                    <IconButton
                                        icon={fullScreenModal ? 'fullscreen_exit' : 'fullscreen'}
                                        onClick={() => {
                                            setFullScreenModal(prev => !prev);
                                        }}
                                        dataCy="fullscreen-modal"
                                    />
                                ) : null}
                                {hideCloseBtn ? null : (
                                    <IconButton icon="close" onClick={onHideModal} dataCy="close-modal" />
                                )}
                            </Layout>
                        </Layout>
                    </Layout>
                    {tabLinks ? <TabsContainer>{tabLinks}</TabsContainer> : null}
                    {underNavigationComponent}
                </Layout>
            }
            content={children}
            actions={
                <Layout gap="--Spacing-100">
                    {hideCancelBtn ? null : (
                        <Button onClick={onHideModal} variant="text" dataCy="button--cancel">
                            Cancel
                        </Button>
                    )}
                    {additionalButton && additionalButton}
                    {showSaveButton && (
                        <Button
                            variant="filled"
                            onClick={onSave}
                            dataCy={saveButtonDataCy}
                            disabled={disabledSaveButton || loading}
                            tooltipText={saveButtonTooltip}
                            tooltipEnterDelay={0}
                        >
                            {saveButtonText}
                        </Button>
                    )}
                </Layout>
            }
            extendedActions={extendFooter}
        />
    );
};

export default CommonModalTemplate;
